import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import {
  DollarSign, Users, Calculator, Calendar, ChevronRight,
  AlertCircle, Loader2, Check, RefreshCw, Info, Scale
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '@/lib/utils';

import { API } from '@/config/api';

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

// Method display names
const methodLabels = {
  equal: 'Equal Split',
  hours: 'Hours-Based',
  weighted_hours: 'Weighted Hours'
};

export default function TipsPage() {
  const { token } = useOutletContext();
  
  // State
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [poolDays, setPoolDays] = useState([]);
  const [activeTab, setActiveTab] = useState('compute');
  
  // Compute state
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedRule, setSelectedRule] = useState('');
  const [manualTipAmount, setManualTipAmount] = useState('');
  const [computing, setComputing] = useState(false);
  const [computeResult, setComputeResult] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  
  // Rule modal state
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [ruleForm, setRuleForm] = useState({
    name: '',
    method: 'hours',
    exclude_managers: true,
    min_shift_minutes: 0
  });
  const [savingRule, setSavingRule] = useState(false);
  
  // Date range for history
  const [historyFrom, setHistoryFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().split('T')[0];
  });
  const [historyTo, setHistoryTo] = useState(new Date().toISOString().split('T')[0]);
  
  const fetchRules = useLatestCallback(async () => {
    try {
      const res = await axios.get(`${API}/labor/tips/rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(res.data.rules || []);
      
      // Set default rule
      const defaultRule = res.data.rules?.find(r => r.is_default);
      if (defaultRule) {
        setSelectedRule(defaultRule.id);
      }
    } catch (error) {
      console.error('Error fetching rules:', error);
    }
  });
  
  const fetchPoolDays = useLatestCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/labor/tips/pool-days`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { from: historyFrom, to: historyTo }
      });
      setPoolDays(res.data.pool_days || []);
    } catch (error) {
      console.error('Error fetching pool days:', error);
    } finally {
      setLoading(false);
    }
  });

  // Fetch data
  useEffect(() => {
    if (token) {
      fetchRules();
      fetchPoolDays();
    }
  }, [token, historyFrom, historyTo, fetchPoolDays, fetchRules]);
  
  // Compute tip pool
  const handleCompute = async () => {
    setComputing(true);
    try {
      const payload = {
        business_date: selectedDate
      };
      
      if (selectedRule) {
        payload.rule_id = selectedRule;
      }
      
      if (manualTipAmount) {
        payload.tip_total_cents = Math.round(parseFloat(manualTipAmount) * 100);
      }
      
      const res = await axios.post(`${API}/labor/tips/pool-days/compute`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setComputeResult(res.data);
      toast.success('Tip pool computed');
    } catch (error) {
      console.error('Error computing tip pool:', error);
      toast.error(error.response?.data?.detail || 'Failed to compute tip pool');
    } finally {
      setComputing(false);
    }
  };
  
  // Finalize tip pool
  const handleFinalize = async () => {
    if (!computeResult?.tip_pool_day_id) return;
    
    setFinalizing(true);
    try {
      await axios.post(`${API}/labor/tips/pool-days/${computeResult.tip_pool_day_id}/finalize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Tip pool finalized!');
      setComputeResult(prev => ({ ...prev, status: 'finalized' }));
      fetchPoolDays();
    } catch (error) {
      console.error('Error finalizing:', error);
      toast.error('Failed to finalize');
    } finally {
      setFinalizing(false);
    }
  };
  
  // Create rule
  const handleCreateRule = async () => {
    if (!ruleForm.name) {
      toast.error('Rule name is required');
      return;
    }
    
    setSavingRule(true);
    try {
      await axios.post(`${API}/labor/tips/rules`, {
        name: ruleForm.name,
        method: ruleForm.method,
        exclude_managers: ruleForm.exclude_managers,
        min_shift_minutes: ruleForm.min_shift_minutes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Rule created');
      setShowRuleModal(false);
      fetchRules();
      setRuleForm({ name: '', method: 'hours', exclude_managers: true, min_shift_minutes: 0 });
    } catch (error) {
      console.error('Error creating rule:', error);
      toast.error('Failed to create rule');
    } finally {
      setSavingRule(false);
    }
  };
  
  // Set default rule
  const handleSetDefaultRule = async (ruleId) => {
    try {
      await axios.post(`${API}/labor/tips/rules/${ruleId}/set-default`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Default rule updated');
      fetchRules();
    } catch (error) {
      toast.error('Failed to set default');
    }
  };

  if (loading && rules.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tips-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Tip Pooling</h1>
          <p className="text-muted-foreground mt-1">Compute and distribute tips among staff</p>
        </div>
        
        <Button onClick={() => setShowRuleModal(true)} variant="outline">
          <Scale className="h-4 w-4 mr-2" />
          New Rule
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="compute">Compute Tips</TabsTrigger>
          <TabsTrigger value="rules">Distribution Rules</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        {/* Compute Tab */}
        <TabsContent value="compute" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Compute Form */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-foreground">Compute Tip Pool</CardTitle>
                <CardDescription>Calculate tip distribution for a day</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Business Date</Label>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={e => setSelectedDate(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Distribution Rule</Label>
                  <Select value={selectedRule} onValueChange={setSelectedRule}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select rule" />
                    </SelectTrigger>
                    <SelectContent>
                      {rules.map(rule => (
                        <SelectItem key={rule.id} value={rule.id}>
                          {rule.name} {rule.is_default && '(Default)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Manual Tip Total (Optional)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Auto from the day's paid orders"
                      value={manualTipAmount}
                      onChange={e => setManualTipAmount(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Leave empty to total the tips on the day's paid orders
                  </p>
                </div>
                
                <Button 
                  className="w-full" 
                  onClick={handleCompute}
                  disabled={computing || !selectedDate}
                >
                  {computing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Calculator className="h-4 w-4 mr-2" />
                  )}
                  Compute Distribution
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            <Card className="lg:col-span-2 bg-card border-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-foreground">Distribution Preview</CardTitle>
                    <CardDescription>
                      {computeResult ? `${computeResult.business_date} • ${computeResult.rule_name}` : 'Run compute to see results'}
                    </CardDescription>
                  </div>
                  {computeResult && (
                    <Badge className={cn(
                      computeResult.status === 'finalized'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'
                    )}>
                      {computeResult.status}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {computeResult ? (
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-foreground">
                          {computeResult.tip_total_display}
                        </p>
                        <p className="text-sm text-muted-foreground">Total Tips</p>
                        {computeResult.tip_source === 'orders' && (
                          <p className="text-xs text-muted-foreground">
                            from {computeResult.order_count ?? 0} paid order{computeResult.order_count === 1 ? '' : 's'} ({computeResult.timezone})
                          </p>
                        )}
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-foreground">
                          {computeResult.allocations?.length || 0}
                        </p>
                        <p className="text-sm text-muted-foreground">Employees</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-foreground">
                          {methodLabels[computeResult.method] || computeResult.method}
                        </p>
                        <p className="text-sm text-muted-foreground">Method</p>
                      </div>
                    </div>

                    {/* Allocations Table */}
                    {computeResult.allocations?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Employee</TableHead>
                            <TableHead className="text-center">Hours Worked</TableHead>
                            <TableHead className="text-center">Weight</TableHead>
                            <TableHead className="text-right">Tip Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {computeResult.allocations.map((alloc, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-medium">{alloc.employee_name}</TableCell>
                              <TableCell className="text-center">
                                {Math.floor(alloc.minutes_worked / 60)}h {alloc.minutes_worked % 60}m
                              </TableCell>
                              <TableCell className="text-center">{alloc.weight_used}x</TableCell>
                              <TableCell className="text-right font-semibold text-green-600 dark:text-green-400">
                                {alloc.amount_display}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No qualified employees for this date</p>
                      </div>
                    )}

                    {/* Finalize Button */}
                    {computeResult.status !== 'finalized' && computeResult.allocations?.length > 0 && (
                      <div className="flex justify-end pt-4 border-t">
                        <Button onClick={handleFinalize} disabled={finalizing}>
                          {finalizing ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4 mr-2" />
                          )}
                          Finalize & Lock
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Calculator className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>Select a date and rule, then click "Compute Distribution"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Rules Tab */}
        <TabsContent value="rules" className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Distribution Rules</CardTitle>
              <CardDescription>Configure how tips are distributed among staff</CardDescription>
            </CardHeader>
            <CardContent>
              {rules.length === 0 ? (
                <div className="text-center py-12">
                  <Scale className="h-12 w-12 mx-auto text-muted-foreground/50" />
                  <h3 className="mt-4 font-semibold text-foreground">No rules yet</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Create a rule to start distributing tips
                  </p>
                  <Button className="mt-4" onClick={() => setShowRuleModal(true)}>
                    Create First Rule
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {rules.map(rule => (
                    <div 
                      key={rule.id}
                      className={cn(
                        "p-4 rounded-lg border border-border bg-card",
                        rule.is_default && "ring-2 ring-primary"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-foreground">{rule.name}</h3>
                            {rule.is_default && (
                              <Badge className="bg-primary/10 text-primary">Default</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Scale className="h-3 w-3" />
                              {methodLabels[rule.method]}
                            </span>
                            {rule.exclude_managers && (
                              <span>Excludes managers</span>
                            )}
                            {rule.min_shift_minutes > 0 && (
                              <span>Min {rule.min_shift_minutes} mins</span>
                            )}
                          </div>
                        </div>
                        {!rule.is_default && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleSetDefaultRule(rule.id)}
                          >
                            Set as Default
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-foreground">Tip Pool History</CardTitle>
                  <CardDescription>Past tip distributions</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={historyFrom}
                    onChange={e => setHistoryFrom(e.target.value)}
                    className="w-36"
                  />
                  <span className="text-muted-foreground">to</span>
                  <Input
                    type="date"
                    value={historyTo}
                    onChange={e => setHistoryTo(e.target.value)}
                    className="w-36"
                  />
                  <Button variant="outline" size="icon" onClick={fetchPoolDays}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {poolDays.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto opacity-30" />
                  <p className="mt-4">No tip pool data for this period</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Rule</TableHead>
                      <TableHead className="text-center">Employees</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {poolDays.map(day => (
                      <TableRow key={day.id}>
                        <TableCell className="font-medium">
                          {new Date(day.business_date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </TableCell>
                        <TableCell>{day.rule_name || '-'}</TableCell>
                        <TableCell className="text-center">{day.allocation_count}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {day.tip_total_display}
                        </TableCell>
                        <TableCell>
                          <Badge className={cn(
                            day.status === 'finalized'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'
                          )}>
                            {day.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Rule Modal */}
      <Dialog open={showRuleModal} onOpenChange={setShowRuleModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Tip Rule</DialogTitle>
            <DialogDescription>
              Define how tips should be distributed
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rule Name *</Label>
              <Input
                value={ruleForm.name}
                onChange={e => setRuleForm({...ruleForm, name: e.target.value})}
                placeholder="e.g., Standard Hours-Based"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Distribution Method</Label>
              <Select value={ruleForm.method} onValueChange={v => setRuleForm({...ruleForm, method: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="equal">Equal Split - Same amount for everyone</SelectItem>
                  <SelectItem value="hours">Hours-Based - Proportional to hours worked</SelectItem>
                  <SelectItem value="weighted_hours">Weighted Hours - By role weight × hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Minimum Shift Duration (minutes)</Label>
              <Input
                type="number"
                value={ruleForm.min_shift_minutes}
                onChange={e => setRuleForm({...ruleForm, min_shift_minutes: parseInt(e.target.value) || 0})}
              />
              <p className="text-xs text-muted-foreground">
                Employees must work at least this many minutes to qualify
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="exclude_managers"
                checked={ruleForm.exclude_managers}
                onChange={e => setRuleForm({...ruleForm, exclude_managers: e.target.checked})}
                className="h-4 w-4"
              />
              <Label htmlFor="exclude_managers">Exclude managers from tip pool</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRuleModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateRule} disabled={savingRule}>
              {savingRule ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Create Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
